
import React, { useState } from 'react';
import { 
  Plus, Search, StickyNote, Trash2, Edit2, X, User, MapPin, 
  Calendar, Filter, Check, Tag, Settings, Palette, Save, 
  Loader2, Info, Clock, ExternalLink, Link as LinkIcon,
  ChevronRight, ChevronLeft, Eye, MessageSquareText,
  FileText, Briefcase, ArrowLeft
} from 'lucide-react';
import { Note, NoteCategory, EntityType, Contact, Location, Inspection } from '../types';
import { supabase, toSnakeCase, getErrorMessage } from '../lib/supabase';

interface NotesProps {
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  categories: NoteCategory[];
  setCategories: React.Dispatch<React.SetStateAction<NoteCategory[]>>;
  contacts: Contact[];
  locations: Location[];
  inspections: Inspection[];
}

const Notes: React.FC<NotesProps> = ({ notes, setNotes, categories, setCategories, contacts, locations, inspections }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const [noteForm, setNoteForm] = useState<Partial<Note>>({
    title: '', content: '', entityType: 'General', entityId: '', categoryId: categories[0]?.id || ''
  });

  const filteredNotes = notes.filter(n => 
    n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedNote = notes.find(n => n.id === selectedNoteId);

  const handleNoteSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteForm.title || !noteForm.content) return;
    setSaving(true);

    try {
      const payload = toSnakeCase(noteForm);
      if (editingNoteId) {
        const { error } = await supabase.from('notes').update(payload).eq('id', editingNoteId);
        if (error) throw error;
        setNotes(prev => prev.map(n => n.id === editingNoteId ? { ...n, ...noteForm } as Note : n));
      } else {
        const { data, error } = await supabase.from('notes').insert([payload]).select().single();
        if (error) throw error;
        setNotes(prev => [{ ...noteForm, id: data.id, createdAt: data.created_at } as Note, ...prev]);
      }
      setIsSidebarOpen(false);
    } catch (err: any) {
      alert(`Save error: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleNoteDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Permanently purge this observation record?')) return;
    try {
      const { error } = await supabase.from('notes').delete().eq('id', id);
      if (error) throw error;
      setNotes(prev => prev.filter(n => n.id !== id));
      if (selectedNoteId === id) setSelectedNoteId(null);
    } catch (err: any) { alert(getErrorMessage(err)); }
  };

  const getEntityInfo = (type: EntityType, id?: string) => {
    if (!id) return null;
    if (type === 'Location') {
      const loc = locations.find(l => l.id === id);
      return { label: loc?.address || 'Unknown Asset', icon: MapPin };
    }
    if (type === 'Contact') {
      const con = contacts.find(c => c.id === id);
      return { label: con?.name || 'Unknown Contact', icon: User };
    }
    if (type === 'Inspection') {
      const ins = inspections.find(i => i.id === id);
      const prop = locations.find(l => l.id === ins?.propertyId);
      return { label: `Session @ ${prop?.address || 'Unknown'}`, icon: FileText };
    }
    return null;
  };

  const getCategory = (id: string) => categories.find(c => c.id === id);

  return (
    <div className="animate-in fade-in duration-300">
      {selectedNoteId && selectedNote ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSelectedNoteId(null)} 
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h3 className="text-lg font-bold text-slate-800 leading-none">{selectedNote.title}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1.5">
                  Registry Log Entry • {new Date(selectedNote.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => { setEditingNoteId(selectedNote.id); setNoteForm({...selectedNote}); setIsSidebarOpen(true); }}
                className="px-4 py-2 text-xs font-bold bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all flex items-center gap-2 shadow-sm"
              >
                <Edit2 size={14} /> Modify
              </button>
              <button 
                onClick={(e) => handleNoteDelete(selectedNote.id, e)}
                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all shadow-sm"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Wider Left Sidebar for Metadata */}
            <div className="space-y-6">
              <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-8">
                <div>
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">Registry Context</h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-slate-500">
                      <Calendar size={14} />
                      <span className="text-xs font-semibold">{new Date(selectedNote.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span 
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${getCategory(selectedNote.categoryId)?.color || 'bg-slate-100 text-slate-500'}`}
                      >
                        {getCategory(selectedNote.categoryId)?.name || 'General Observation'}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedNote.entityId && (
                  <div className="pt-6 border-t border-slate-100">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">Linked Artifact</h4>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-center gap-3">
                      <div className="p-2.5 bg-white rounded-lg shadow-sm text-blue-500">
                        {React.createElement(getEntityInfo(selectedNote.entityType, selectedNote.entityId)?.icon || LinkIcon, { size: 18 })}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">
                          {getEntityInfo(selectedNote.entityType, selectedNote.entityId)?.label}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          {selectedNote.entityType}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* Note Content Section on the Right */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-10 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden h-full min-h-[400px]">
                <div className="absolute bottom-0 right-0 p-10 text-slate-100/50 -rotate-12 select-none pointer-events-none translate-x-1/4 translate-y-1/4">
                  <MessageSquareText size={200} />
                </div>
                <div className="relative z-10">
                  <p className="text-slate-700 text-lg font-medium leading-relaxed whitespace-pre-wrap">
                    {selectedNote.content}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-xl font-bold text-slate-800 tracking-tight uppercase">Knowledge Registry</h3>
              <p className="text-sm text-slate-500 font-medium">Technical observations and field intelligence.</p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Search observation notes..." 
                  className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none shadow-sm transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button 
                onClick={() => { setEditingNoteId(null); setNoteForm({ title: '', content: '', entityType: 'General', entityId: '', categoryId: categories[0]?.id || '' }); setIsSidebarOpen(true); }} 
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm hover:bg-slate-800 transition-all flex items-center gap-2 whitespace-nowrap"
              >
                <Plus size={16} /> New Record
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Observation</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest hidden md:table-cell">Classification</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest hidden lg:table-cell">Linked Target</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Logged At</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredNotes.length > 0 ? filteredNotes.map(note => {
                  const category = getCategory(note.categoryId);
                  const entity = getEntityInfo(note.entityType, note.entityId);
                  return (
                    <tr 
                      key={note.id} 
                      onClick={() => setSelectedNoteId(note.id)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      <td className="px-6 py-4 max-w-md">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors shrink-0">
                            <StickyNote size={14} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800 truncate">{note.title}</p>
                            <p className="text-xs text-slate-400 font-medium truncate mt-0.5">{note.content.substring(0, 60)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span 
                          className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${category?.color || 'bg-slate-100 text-slate-500'}`}
                        >
                          {category?.name || 'General'}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        {entity ? (
                          <div className="flex items-center gap-2 text-slate-600">
                            <entity.icon size={12} className="text-slate-300" />
                            <span className="text-xs font-medium truncate max-w-[150px]">{entity.label}</span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">No Link</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-medium text-slate-500">{new Date(note.createdAt).toLocaleDateString()}</p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={(e) => handleNoteDelete(note.id, e)} 
                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Purge Record"
                          >
                            <Trash2 size={16} />
                          </button>
                          <div className="inline-flex items-center justify-center p-2 text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 rounded-lg transition-all">
                            <ChevronRight size={16} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={5} className="py-20 text-center text-slate-400">
                      <Search size={32} className="mx-auto mb-3 opacity-20" />
                      <p className="text-sm font-medium italic">No indexed observations matching criteria.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Note Creation/Editor Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 transition-opacity duration-200" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">
              {editingNoteId ? 'Modify Observation Record' : 'Log New Observation'}
            </h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400 transition-all"><X size={20} /></button>
          </div>
          <form onSubmit={handleNoteSave} className="flex-1 p-8 space-y-6 overflow-y-auto no-scrollbar">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Observation Headline</label>
              <input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={noteForm.title} onChange={(e)=>setNoteForm({...noteForm, title: e.target.value})} placeholder="Title of the finding..." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Classification</label>
                <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:ring-1 focus:ring-blue-500" value={noteForm.categoryId} onChange={(e)=>setNoteForm({...noteForm, categoryId: e.target.value})}>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Artifact Link</label>
                <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:ring-1 focus:ring-blue-500" value={noteForm.entityType} onChange={(e)=>setNoteForm({...noteForm, entityType: e.target.value as EntityType, entityId: ''})}>
                  <option value="General">No Association</option>
                  <option value="Location">Property/Asset</option>
                  <option value="Contact">Stakeholder</option>
                  <option value="Inspection">Session</option>
                </select>
              </div>
            </div>

            {noteForm.entityType !== 'General' && (
              <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Target Entity</label>
                <select required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:ring-1 focus:ring-blue-500" value={noteForm.entityId} onChange={(e)=>setNoteForm({...noteForm, entityId: e.target.value})}>
                  <option value="">Choose target...</option>
                  {noteForm.entityType === 'Location' && locations.map(l => <option key={l.id} value={l.id}>{l.address} ({l.name})</option>)}
                  {noteForm.entityType === 'Contact' && contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  {noteForm.entityType === 'Inspection' && inspections.map(i => <option key={i.id} value={i.id}>{locations.find(l => l.id === i.propertyId)?.address} • {i.date}</option>)}
                </select>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Detailed Content</label>
              <textarea required rows={8} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 leading-relaxed outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 transition-all resize-none" value={noteForm.content} onChange={(e)=>setNoteForm({...noteForm, content: e.target.value})} placeholder="Detailed description of observations..." />
            </div>
          </form>
          <div className="p-6 border-t border-slate-100 flex gap-3 shadow-inner">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-3 text-xs font-bold text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all uppercase tracking-widest">Cancel</button>
            <button disabled={saving} onClick={handleNoteSave} className="flex-[2] py-3 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-sm uppercase tracking-widest">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Commit Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notes;
